// Client-side visitor tracking. Persists a visitor_id in localStorage and
// a session_id in sessionStorage (30-min idle window). All events are
// forwarded to the `track-visitor` edge function which enriches with IP
// geo, updates the visitor profile, and inserts into visitor_events.

const VID_KEY = "egs_visitor_id";
const SID_KEY = "egs_session_id";
const SID_TS_KEY = "egs_session_ts";
const SESSION_TIMEOUT = 30 * 60 * 1000;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

function rid(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getVisitorId(): string {
  let id = localStorage.getItem(VID_KEY);
  if (!id) { id = rid("v"); localStorage.setItem(VID_KEY, id); }
  return id;
}

export function getSessionId(): { id: string; isNew: boolean } {
  const now = Date.now();
  const ts = parseInt(sessionStorage.getItem(SID_TS_KEY) || "0", 10);
  let id = sessionStorage.getItem(SID_KEY);
  let isNew = false;
  if (!id || !ts || now - ts > SESSION_TIMEOUT) {
    id = rid("s"); isNew = true;
    sessionStorage.setItem(SID_KEY, id);
  }
  sessionStorage.setItem(SID_TS_KEY, String(now));
  return { id, isNew };
}

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
  };
}

let sessionStarted = false;

export async function track(
  event_type: string,
  data: { path?: string; label?: string; metadata?: Record<string, unknown> } = {},
) {
  try {
    // Skip admin and immersive routes
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;

    const visitor_id = getVisitorId();
    const { id: session_id, isNew } = getSessionId();
    const utm = getUTM();
    const path = data.path ?? window.location.pathname;

    // Auto-fire session_started once per session
    if (isNew && !sessionStarted) {
      sessionStarted = true;
      await send({
        visitor_id, session_id,
        event_type: "session_started",
        path,
        landing_page: path,
        referrer: document.referrer || undefined,
        ...utm,
      });
    }

    await send({
      visitor_id, session_id, event_type,
      path, label: data.label, metadata: data.metadata,
      referrer: document.referrer || undefined,
      ...utm,
    });
  } catch { /* silent */ }
}

async function send(payload: Record<string, unknown>) {
  const url = `${SUPABASE_URL}/functions/v1/track-visitor`;
  const body = JSON.stringify(payload);
  // Prefer sendBeacon for page-unload reliability; fall back to fetch w/ keepalive.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      // sendBeacon doesn't allow auth headers; fall through to fetch for non-trivial events.
      if (payload.event_type === "session_ended") {
        navigator.sendBeacon(url + `?apikey=${ANON_KEY}`, blob);
        return;
      }
    }
  } catch { /* ignore */ }
  await fetch(url, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      "apikey": ANON_KEY,
      "Authorization": `Bearer ${ANON_KEY}`,
    },
    body,
  });
}

// Convenience helpers
export const trackCta = (label: string, placement: string) =>
  track("book_call_clicked", { label, metadata: { placement } });
export const trackPhone = (placement: string) =>
  track("call_link_clicked", { label: "phone", metadata: { placement } });
export const trackScroll = (depth: number) =>
  track("scroll_milestone", { metadata: { depth_pct: depth } });
export const trackVideo = (event: "started" | "completed" | "progress", videoId: string, extra: Record<string, unknown> = {}) =>
  track(`video_${event}`, { label: videoId, metadata: extra });
export const trackFaq = (open: boolean, question: string) =>
  track(open ? "faq_opened" : "faq_closed", { label: question.slice(0, 200) });
